import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExclamationCircleIcon,
  PhotoIcon,
  BoldIcon,
  ItalicIcon,
  ListBulletIcon,
  NumberedListIcon
} from '@heroicons/react/24/outline';
import { useFAQs } from '../../contexts/FAQContext';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import FontSize from '../../extensions/fontSize';
import { uploadImageToStorage } from '../../utils/imageUpload';

interface FAQFormData {
  question: string;
  answer: string;
  sort_order: number;
  is_published: boolean;
  author: string;
}

const FAQForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addFAQ, updateFAQ, getFAQ } = useFAQs();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<FAQFormData>({
    question: '',
    answer: '',
    sort_order: 0,
    is_published: true,
    author: '관리자'
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FAQFormData, string>>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color.configure({
        types: ['textStyle'],
      }),
      FontSize,
      Placeholder.configure({
        placeholder: '답변 내용을 입력하세요...',
      }),
      Image.configure({
        inline: false, // 블록 요소로 처리하여 선택/편집 용이하게
        allowBase64: false,
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
    ],
    content: formData.answer,
    onUpdate: ({ editor }) => {
      setFormData({ ...formData, answer: editor.getHTML() });
      if (errors.answer) {
        setErrors({ ...errors, answer: '' });
      }
    },
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && editor) {
      try {
        const imageUrl = await uploadImageToStorage(file, 'faq-images');
        editor.chain().focus().setImage({ src: imageUrl }).run();
        console.log('FAQ image uploaded successfully:', imageUrl);
      } catch (error) {
        console.error('Failed to upload FAQ image:', error);
        alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  const addImageFromButton = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: name === 'sort_order' ? Number(value) : value });
    if (errors[name as keyof FAQFormData]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof FAQFormData, string>> = {};

    if (!formData.question) newErrors.question = '질문은 필수입니다.';
    if (!formData.answer || formData.answer === '<p></p>') newErrors.answer = '답변은 필수입니다.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        if (isEditMode && id) {
          await updateFAQ(Number(id), formData);
          alert('FAQ가 성공적으로 수정되었습니다!');
        } else {
          await addFAQ(formData);
          alert('FAQ가 성공적으로 등록되었습니다!');
        }
        navigate('/admin/faqs');
      } catch (error) {
        console.error('Failed to submit FAQ:', error);
        alert('FAQ 저장에 실패했습니다.');
      }
    }
  };

  // Edit mode: Load existing FAQ data
  useEffect(() => {
    if (isEditMode && id) {
      const faq = getFAQ(Number(id));
      if (faq) {
        setFormData({
          question: faq.question,
          answer: faq.answer,
          sort_order: faq.sort_order,
          is_published: faq.is_published ?? true,
          author: faq.author
        });
        if (editor) {
          editor.commands.setContent(faq.answer);
        }
      }
    }
  }, [isEditMode, id, getFAQ, editor]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {isEditMode ? 'FAQ 수정' : 'FAQ 등록'}
        </h1>
        <p className="text-gray-600">
          {isEditMode ? 'FAQ 내용을 수정합니다.' : '새로운 FAQ를 등록합니다.'}
          <span className="text-red-500">*</span> 표시는 필수 항목입니다.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8">
        {/* 기본 정보 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">기본 정보</h2>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                질문 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="question"
                value={formData.question}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.question ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="자주 묻는 질문을 입력하세요"
              />
              {errors.question && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.question}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  정렬 순서
                </label>
                <input
                  type="number"
                  name="sort_order"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="0"
                  min="0"
                />
                <p className="mt-1 text-xs text-gray-500">숫자가 낮을수록 위에 표시됩니다.</p>
              </div>

              <div className="flex items-center pt-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_published"
                    checked={formData.is_published}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        is_published: e.target.checked
                      });
                    }}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm font-medium text-gray-700">공개</span>
                </label>
                <p className="ml-3 text-xs text-gray-500">체크하면 사용자에게 공개됩니다.</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                답변 <span className="text-red-500">*</span>
              </label>
              <div className={`border rounded-lg ${errors.answer ? 'border-red-500' : 'border-gray-300'} bg-white`}>
                {/* 에디터 툴바 */}
                <div className="flex items-center gap-0.5 p-2 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('bold') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Bold"
                  >
                    <BoldIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('italic') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Italic"
                  >
                    <ItalicIcon className="h-5 w-5" />
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={`px-2.5 py-1.5 rounded hover:bg-gray-100 transition-colors font-semibold text-sm ${
                      editor?.isActive('heading', { level: 2 }) ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Heading 2"
                  >
                    H2
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                    className={`px-2.5 py-1.5 rounded hover:bg-gray-100 transition-colors font-semibold text-sm ${
                      editor?.isActive('heading', { level: 3 }) ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Heading 3"
                  >
                    H3
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  {/* 폰트 크기 */}
                  <select
                    value={editor?.getAttributes('textStyle').fontSize || ''}
                    onChange={(e) => {
                      if (!editor) return;
                      const size = e.target.value;
                      if (size) {
                        editor.chain().focus().setFontSize(size).run();
                      } else {
                        editor.chain().focus().unsetFontSize().run();
                      }
                    }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 bg-white text-gray-600"
                    title="글자 크기"
                  >
                    <option value="">기본</option>
                    <option value="12px">12px 작게</option>
                    <option value="14px">14px 보통</option>
                    <option value="18px">18px 크게</option>
                    <option value="20px">20px 매우 크게</option>
                    <option value="24px">24px 특대</option>
                  </select>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('bulletList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Bullet List"
                  >
                    <ListBulletIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('orderedList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Numbered List"
                  >
                    <NumberedListIcon className="h-5 w-5" />
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={addImageFromButton}
                    className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
                    title="Insert Image"
                  >
                    <PhotoIcon className="h-5 w-5" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  {/* 색상 버튼들 */}
                  {[
                    { color: '#000000', title: '검정색' },
                    { color: '#6B7280', title: '회색' },
                    { color: '#EF4444', title: '빨간색' },
                    { color: '#F97316', title: '주황색' },
                    { color: '#EAB308', title: '노란색' },
                    { color: '#22C55E', title: '초록색' },
                    { color: '#059669', title: '에메랄드' },
                    { color: '#14B8A6', title: '청록색' },
                    { color: '#3B82F6', title: '파란색' },
                    { color: '#8B5CF6', title: '보라색' },
                  ].map(({ color, title }) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => {
                        if (!editor) return;
                        const { from, to } = editor.state.selection;
                        const text = editor.state.doc.textBetween(from, to, '');
                        if (text) {
                          editor.chain().focus().deleteSelection().insertContent(`<span style="color: ${color}">${text}</span>`).run();
                        }
                      }}
                      className="w-5 h-5 rounded border-2 border-gray-300 hover:border-gray-500 hover:scale-110 transition-all"
                      style={{ backgroundColor: color }}
                      title={title}
                    />
                  ))}
                </div>
                {/* 에디터 본문 */}
                <EditorContent
                  editor={editor}
                  className="prose max-w-none p-4 min-h-[200px] focus:outline-none"
                />
              </div>
              {errors.answer && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.answer}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/faqs')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            {isEditMode ? '수정하기' : '등록하기'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FAQForm;
